import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Modal, Button, Toast, Form } from 'react-bootstrap';
import { useRefreshSingleMealAndGroceryList } from '../hooks/refreshSingleMealAndGroceryList';
import { useUserShowMealDeletionConfirmation } from '../hooks/userPreferencesHelper';
import { useAuth } from '../context/AuthContext';
import { fetchWithCSRF } from '../utils/csrfUtils';
import { useAddSingleMealToMealPlan } from '../hooks/addSingleMealToMealPlan';
import { getRecipeImageUrl, getIconUrl, getS3ImageUrl } from '../utils/imageUtils';



function MealCardMobile({ meal, onStartCooking, isInPast, onMealRefresh, onGroceryListRefresh }) {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [showModal, setShowModal] = useState(false);
    const [servingSize, setServingSize] = useState(meal.serving_size || 1);
    const [errorRefreshingMeal, setErrorRefreshingMeal] = useState(false);
    const [errorRefreshingMealMessage, setErrorRefreshingMealMessage] = useState('');
    const [servingSizeUpdateTimeout, setServingSizeUpdateTimeout] = useState(null);
    const [isUpdatingServingSize, setIsUpdatingServingSize] = useState(false);
    const { refreshSingleMealAndGroceryList, isRefreshing, error } = useRefreshSingleMealAndGroceryList();
    const { pgUser, sessionToken, updatePgUserProperty } = useAuth();
    const { updateUserShowMealDeletionConfirmation, isLoading: isUpdatingShowMealDeletionConfirmation } = useUserShowMealDeletionConfirmation();
    const [showDeletionConfirmation, setShowDeletionConfirmation] = useState(pgUser?.show_meal_deletion_confirmation_modal ?? true);
    const { addSingleMealToMealPlan, isLoading: isAddingSingleMeal, error: addSingleMealError } = useAddSingleMealToMealPlan();
    const [recentlyDeletedMealObjectArray, setRecentlyDeletedMealObjectArray] = useState([]); //for storing the meal object that was deleted from the meal plan, in case user wants to undo the deletion
    //recentlyDeletedMealObject stores the following properties: meal_id, recipe_id, serving_size, meal_of_day, planned_cook_date, actual_cook_date, has_been_cooked, is_favorite

    // Sync local state with pgUser when it changes
    useEffect(() => {
        if (pgUser?.show_meal_deletion_confirmation_modal !== undefined) {
            setShowDeletionConfirmation(pgUser.show_meal_deletion_confirmation_modal);
        }
    }, [pgUser?.show_meal_deletion_confirmation_modal]);

    // Debounced serving size update
    useEffect(() => {
        if (servingSizeUpdateTimeout) {
            clearTimeout(servingSizeUpdateTimeout);
        }

        if (servingSize !== meal.serving_size) {
            const timeout = setTimeout(async () => {
                await handleServingSizeUpdate();
            }, 500); // Wait 0.5 second after last change
            setServingSizeUpdateTimeout(timeout);
        }

        return () => {
            if (servingSizeUpdateTimeout) {
                clearTimeout(servingSizeUpdateTimeout);
            }
        };
    }, [servingSize, meal.serving_size]);

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (servingSizeUpdateTimeout) {
                clearTimeout(servingSizeUpdateTimeout);
            }
        };
    }, []);

    const handleCardClick = (e) => {
        // Open meal steps when card is clicked
        if (!meal.has_been_cooked) {
            // Navigate to meal plan with mealId parameter to show meal steps
            navigate(`/meal-plan?mealId=${meal.id}`);
            // Call parent callback to set selected meal
            if (onStartCooking) {
                onStartCooking(meal);
            }
        }
    };

    const showErrorRefreshingMealToast = (message) => {
        setErrorRefreshingMealMessage(message);
        setErrorRefreshingMeal(true);
        
        // Auto-hide after 5 seconds
        setTimeout(() => {
            setErrorRefreshingMeal(false);
        }, 5000);
    };

    const handleModalClose = () => setShowModal(false);

    const handleTrashClick = (e) => {
        e.stopPropagation();
        if (!meal.has_been_cooked && !isInPast) {
            // Check if user wants to see confirmation modal
            if (showDeletionConfirmation) {
                setShowModal(true);
            } else {
                // Skip modal and delete directly
                handleDeleteConfirm();
            }
        }
    };

    const handleRefreshMeal = async (e) => {
        e.stopPropagation();

        if (!sessionToken) {
            throw new Error('No valid session found');
        }

        if (!meal.has_been_cooked && !isInPast) {
            try {
                // Wait for the backend to complete the meal refresh
                await refreshSingleMealAndGroceryList(meal.id, meal.app_user_id);
                // Only call onMealRefresh after the backend has finished
                if (onMealRefresh) {
                    onMealRefresh();
                }
                // Show toast notification
                if (onGroceryListRefresh) {
                    onGroceryListRefresh("Grocery list updated");
                }
            } catch (error) {
                console.error('Failed to refresh meal:', error);
                showErrorRefreshingMealToast('Error refreshing meal. Please refresh the page and try again.');
            }
        }
    };

    const handleDeleteConfirm = async () => {
        
        try {        
            
            if (!sessionToken) {
                throw new Error('No valid session found');
            }

            console.log("about to call delete_and_refresh with meal_id: ", meal.id, " and appUserId: ", pgUser.id + " and sessionToken: ", sessionToken);
            
            const response = await fetchWithCSRF('/api/v1/meal/delete_and_refresh', {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${sessionToken}`
                },
                body: JSON.stringify({ 
                    meal_id: meal.id,
                }),
            });
            
            const data = await response.json();
            
            if (!response.ok) {
                throw new Error(data.error || 'Failed to remove meal');
            }

            console.log('Meal recipe removed:', data);
            setRecentlyDeletedMealObjectArray([...recentlyDeletedMealObjectArray, meal]);
            handleModalClose();
            if (onMealRefresh) {
                onMealRefresh();
            }
            // Show toast notification with undo button
            if (onGroceryListRefresh) {
                onGroceryListRefresh(<>Grocery list updated <button onClick={() => handleUndoDelete([meal])} className="btn btn-link align-baseline" style={{ textDecoration: 'none', color: '#fff', border: '1px solid #fff', padding: '2px 8px', borderRadius: '4px' }}>Undo</button></>);
            }
        } catch (error) {
            console.error('Error removing meal:', error);
            showErrorRefreshingMealToast('Error removing meal. Please refresh the page and try again.');
        }
    };

    const handleServingSizeUpdate = async () => {
        try {
            setIsUpdatingServingSize(true);
            if (!sessionToken) {
                throw new Error('No valid session found');
            }

            const response = await fetchWithCSRF('/api/v1/meal/serving_size_and_refresh', {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${sessionToken}`
                },
                body: JSON.stringify({
                    meal_id: meal.id,
                    serving_size: servingSize,
                }),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || 'Failed to update serving size');
            }

            console.log('Serving size updated:', data);
            if (onMealRefresh) {
                onMealRefresh();
            }
            // Show toast notification
            if (onGroceryListRefresh) {
                onGroceryListRefresh('Serving size and grocery list updated');
            }
        } catch (error) {
            console.error('Error updating serving size:', error);
            showErrorRefreshingMealToast('Error updating serving size. Please refresh the page and try again.');
        } finally {
            setIsUpdatingServingSize(false);
        }
    };

    const handleServingSizeChange = (newSize) => {
        if (newSize >= 1 && newSize <= 20) { // Reasonable limits
            setServingSize(newSize);
        }
    };

    const handleUndoDelete = async (deletedMealObjectArray) => {
        try {
            // Validate that we have the required properties
            for (const deletedMealObject of deletedMealObjectArray) {
                if (!deletedMealObject.meal_plan_id) {
                    throw new Error('Missing meal_plan_id for undo operation');
                }
                if (!deletedMealObject.position) {
                    deletedMealObject.position = null;
                }
                console.log('deletedMealObject.position: ', deletedMealObject.position);
                console.log('deletedMealObject.meal_plan_id: ', deletedMealObject.meal_plan_id);
                console.log('deletedMealObject.meal_of_day: ', deletedMealObject.meal_of_day);
                console.log('deletedMealObject.serving_size: ', deletedMealObject.serving_size);
                console.log('deletedMealObject.recipe_id: ', deletedMealObject.recipe_id);
                await addSingleMealToMealPlan(
                    deletedMealObject.meal_plan_id, 
                    pgUser.id, 
                    deletedMealObject.meal_of_day, 
                    deletedMealObject.serving_size, 
                    deletedMealObject.recipe_id,
                    deletedMealObject.position
                );
            }
            
            // Clear the deleted meals array
            setRecentlyDeletedMealObjectArray([]);
            
            // Refresh the meal plan to show the restored meal
            if (onMealRefresh) {
                onMealRefresh();
            }
            
            // Show success toast
            if (onGroceryListRefresh) {
                onGroceryListRefresh('Meal restored and grocery list updated');
            }
        } catch (error) {
            console.error('Error undoing delete:', error);
            showErrorRefreshingMealToast('Error undoing delete. Please refresh the page and try again.');
        }
    };

    // Determine card styling based on cooked status and past meal plan
    const cardClassName = `meal-card-mobile ${meal.has_been_cooked || isInPast ? 'cooked' : ''}`;

    return (
        <>
            <div className={cardClassName} onClick={handleCardClick}>
                {/* Delete button in top left corner */}
                <div 
                    className={`delete-meal-corner-mobile ${meal.has_been_cooked || isInPast ? 'cooked' : ''}`} 
                    onClick={handleTrashClick}
                    style={{ 
                        cursor: (meal.has_been_cooked || isInPast) ? 'not-allowed' : 'pointer',
                    }}
                >
                    <img 
                        src={getIconUrl('x-outline.svg')} 
                        alt="Delete Meal" 
                        style={{ 
                            width: '20px', 
                            height: '20px'
                        }}
                    />
                </div>

                {/* Cooked/Did Not Cook badges */}
                {(meal.has_been_cooked) && (
                    <div className="cooked-badge-mobile">
                        <img src={getIconUrl('checkmark.svg')} alt="Completed" />
                    </div>
                )}
                {(!meal.has_been_cooked && isInPast) && (
                    <div className="did-not-cook-badge-mobile">
                        <img src={getIconUrl('xmark.svg')} alt="Did Not Cook" />
                    </div>
                )}

                {/* Image on the left */}
                <div className="meal-card-mobile-image">
                    <img 
                        src={getRecipeImageUrl(meal.recipe_id)}
                        onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = getS3ImageUrl('images/recipeImages/placeholder_recipe.jpg');
                        }}
                        alt={meal.recipe_name}
                    />
                </div>

                {/* Content in the middle */}
                <div className="meal-card-mobile-content">
                    <h5>{meal.recipe_name}</h5>
                    {meal.total_time && (
                        <div className="meal-card-mobile-time">
                            {meal.total_time} min
                        </div>
                    )}
                    {/* Serving Size Adjustment */}
                    {meal.serving_size && (
                        <div className="meal-card-mobile-servings">
                            <div className="d-flex align-items-center gap-1">
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleServingSizeChange(servingSize - 1);
                                    }}
                                    disabled={meal.has_been_cooked || isInPast || servingSize <= 1}
                                    style={{ minWidth: '1.3rem', height: '1.3rem', padding: '0', fontSize: '0.7rem' }}
                                >
                                    −
                                </Button>
                                <span
                                    style={{
                                        minWidth: '1.5rem',
                                        textAlign: 'center',
                                        fontSize: '0.8rem',
                                        fontWeight: '500',
                                        opacity: isUpdatingServingSize ? 0.6 : 1
                                    }}
                                >
                                    {servingSize}
                                    {isUpdatingServingSize && <span style={{ fontSize: '0.6rem', marginLeft: '2px' }}>⟳</span>}
                                </span>
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleServingSizeChange(servingSize + 1);
                                    }}
                                    disabled={meal.has_been_cooked || isInPast || servingSize >= 20}
                                    style={{ minWidth: '1.3rem', height: '1.3rem', padding: '0', fontSize: '0.7rem' }}
                                >
                                    +
                                </Button>
                            </div>
                            <span style={{ fontSize: '0.7rem', color: '#666' }}>servings</span>
                        </div>
                    )}
                </div>

                {/* Actions on the right */}
                <div className="meal-card-mobile-actions">
                    {/* Swap Button */}
                    <Button
                        variant=""
                        size="sm"
                        onClick={handleRefreshMeal}
                        disabled={meal.has_been_cooked || isInPast}
                        className="meal-card-mobile-swap-btn"
                    >
                        <img 
                            src={getIconUrl('refresh_52cd92.svg')} 
                            alt="Swap Meal" 
                            style={{ 
                                width: '14px', 
                                height: '14px',
                                paddingBottom: '2px',
                                animation: isRefreshing ? 'spin 1s linear infinite' : 'none'
                            }}
                        />
                        <span>Swap</span>
                    </Button>
                </div>
            </div>

            {/* Error Refreshing Toast Notification */}
            <Toast 
                show={errorRefreshingMeal} 
                onClose={() => setErrorRefreshingMeal(false)}
                style={{
                    position: 'fixed',
                    top: '10px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    zIndex: 9999,
                    backgroundColor: '#dc3545',
                    border: 'none',
                    boxShadow: '0 4px 12px rgba(0,0,255,0.15)',
                    width: '50%'
                }}
            >
                <Toast.Header style={{ 
                    backgroundColor: '#dc3545', 
                    borderBottom: '1px solid rgba(255,255,255,0.2)',
                    display: 'flex',
                    justifyContent: 'center',
                    position: 'relative',
                    width: '100%'
                }}>
                    <strong style={{ color: 'white' }}>{errorRefreshingMealMessage}</strong>
                </Toast.Header>
            </Toast>

            <Modal show={showModal} onHide={handleModalClose}>
                <Modal.Header closeButton>
                    <Modal.Title>Remove {meal.recipe_name}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>Are you sure you want to remove {meal.recipe_name} from your meal plan?</p>
                </Modal.Body>
                <Modal.Footer style={{ flexDirection: 'column', alignItems: 'stretch' }}>
                    <div className="d-flex justify-content-end gap-2 mb-2">
                        <Button variant="secondary" onClick={handleModalClose}>
                            Cancel
                        </Button>
                        <Button variant="danger" onClick={handleDeleteConfirm}>
                            Remove
                        </Button>
                    </div>
                    {(showModal || showDeletionConfirmation) && (
                        <div style={{ textAlign: 'left' }}>
                            <Form.Check
                                type="checkbox"
                                label="Don't ask again"
                                className="custom-checkbox"
                                checked={!showDeletionConfirmation}
                                onChange={(e) => {
                                    const shouldShowConfirmation = !e.target.checked;
                                    // Update local state immediately for responsive UI
                                    setShowDeletionConfirmation(shouldShowConfirmation);
                                    // Update global pgUser so all MealCard instances get the new value
                                    updatePgUserProperty('show_meal_deletion_confirmation_modal', shouldShowConfirmation);
                                    // Update backend
                                    updateUserShowMealDeletionConfirmation(shouldShowConfirmation);
                                }}
                            />
                        </div>
                    )}
                </Modal.Footer>
            </Modal>
        </>

        
    );
}

export default MealCardMobile;

